import React, { useMemo } from 'react';

import { Box, Button } from '@material-ui/core';

import {
  ActivateSubscriptionDialog,
  ButtonTitleWithLoading,
  ConfirmDialog,
  SuccessSubscriptionDialog,
  VerifyYourIdentityDialog,
} from '@vyce/core/src/components';
import { SubscriptionInfo } from '@vyce/core/src/views/subscription/components/SubscriptionInfo';
import { FormConfirmSubscriptionDialog } from '@vyce/core/src/views/forms/components/FormConfirmSubscriptionDialog';

import { useStyles } from './styles';
import { CancelSubscriptionReasonDialog } from './components/CancelSubscriptionReaasonDialog';
import { ManageSubscriptionDialog } from './components/ManageSubscriptionDialog';
import { useSubscriptionData } from '../../modules/subscriptionInfoModule/hooks/useSubscriptionData';
import { ConfirmMessagingSubscriptionDialog } from '../messages/components/ConfirmMessagingSubscriptionDialog';

export const EmployerSubscription: React.FC = () => {
  const classes = useStyles();
  const {
    selectedCompany,
    isSuccessDialogOpen,
    isConfirmDialogOpen,
    isCancelReasonDialogOpen,
    isActivateSubscriptionDialogOpen,
    isLoginDialogOpen,
    loading,
    isManageDialogOpen,
    email,
    selectedCompanyAppData,
    billingData,
    closeSuccessDialog,
    closeConfirmDialog,
    cancelSubscription,
    resumeSubscription,
    resumeLoading,
    subscriptionEndDate,
    closeCancelReasonDialog,
    closeActivateSubscriptionDialog,
    subscribe,
    openConfirmDialog,
    openLoginDialog,
    closeManageDialog,
    closeLoginDialog,
    handleNext,
    openActivateSubscriptionDialog,
    openManageDialog,
    isMessagingSubscriptionDialogOpen,
    closeMessagingSubscriptionDialog,
    addMessagingSubscription,
    loadingMessagingSubscription,
    openMessagingSubscriptionDialog,
    openRemoveMessagingSubscriptionDialog,
    isRemoveMessagingSubscriptionDialogOpen,
    closeRemoveMessagingSubscriptionDialog,
    removeMessagingSubscription,
    messagingSubscription,
    formsSubscription,
    openFormsSubscriptionDialog,
    openRemoveFormsSubscriptionDialog,
    isFormsSubscriptionDialogOpen,
    closeFormsSubscriptionDialog,
    addFormsSubscription,
    loadingFormsSubscription,
  } = useSubscriptionData();

  const teamsText = useMemo(() => {
    return selectedCompanyAppData?.teams?.length ? 'Add team members now' : 'Create a Team';
  }, [selectedCompanyAppData?.teams?.length]);

  const payrollText = useMemo(() => {
    return selectedCompanyAppData?.paySchedules?.length ? 'Add to Payroll' : 'Set Up Payroll';
  }, [selectedCompanyAppData?.paySchedules]);

  const timeText = useMemo(() => {
    return selectedCompanyAppData?.locations?.length
      ? 'Add to Time & Attendance'
      : 'Set up Time & Attendance';
  }, [selectedCompanyAppData?.locations]);

  const { subscription } = selectedCompanyAppData || {};
  const isCancellationPending = !!subscription?.active && !!subscription?.cancel_at_period_end;
  // Any earlier subscription means no trial this time.
  const wasSubscribed = !!selectedCompany?.subscribed_company || !!subscription?.uuid;

  return (
    <>
      <ConfirmMessagingSubscriptionDialog
        open={isMessagingSubscriptionDialogOpen}
        setOpen={closeMessagingSubscriptionDialog}
        confirmFunction={addMessagingSubscription}
        loading={loadingMessagingSubscription}
      />
      <FormConfirmSubscriptionDialog
        open={isFormsSubscriptionDialogOpen}
        setOpen={closeFormsSubscriptionDialog}
        confirmFunction={addFormsSubscription}
        loading={loadingFormsSubscription}
      />
      <ConfirmDialog
        handleClose={() => {
          closeRemoveMessagingSubscriptionDialog();
        }}
        open={isRemoveMessagingSubscriptionDialogOpen}
        subtitle={`Are you sure you want to deactivate the Messages module?`}
        confirmText="Yes"
        title="Deactivate Messages module"
        loading={loadingMessagingSubscription}
        handleConfirm={removeMessagingSubscription}
      />
      {selectedCompany && (
        <SubscriptionInfo
          billingData={billingData}
          companyName={selectedCompany.name}
          loading={selectedCompanyAppData?.companyDataLoading}
          subscription={selectedCompanyAppData?.subscription}
          messagingSubscription={messagingSubscription}
          formsSubscription={formsSubscription}
          openFormsSubscriptionDialog={openFormsSubscriptionDialog}
          openRemoveFormsSubscriptionDialog={openRemoveFormsSubscriptionDialog}
          openMessagingSubscriptionDialog={openMessagingSubscriptionDialog}
          openActivateSubscriptionDialog={openActivateSubscriptionDialog}
          openRemoveMessagingSubscriptionDialog={openRemoveMessagingSubscriptionDialog}
        >
          <Box display="flex" justifyContent="flex-end">
            {!subscription?.active && (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => openActivateSubscriptionDialog()}
                className={classes.subscribeButton}
              >
                Subscribe Now
              </Button>
            )}
            {isCancellationPending && (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                style={{ width: 177 }}
                disabled={resumeLoading}
                onClick={resumeSubscription}
              >
                <ButtonTitleWithLoading title="Reactivate" loaderVariant="paper" loading={resumeLoading} />
              </Button>
            )}
            {subscription?.active && !subscription?.ended && !isCancellationPending && (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                style={{ width: 177 }}
                onClick={() => openManageDialog()}
              >
                Manage subscription
              </Button>
            )}
          </Box>
        </SubscriptionInfo>
      )}

      <SuccessSubscriptionDialog
        open={isSuccessDialogOpen}
        setOpen={closeSuccessDialog}
        teamsText={teamsText}
        payrollText={payrollText}
        timeText={timeText}
        withPayroll
        withTeams
        withTime
        withForms
      />

      <ConfirmDialog
        handleClose={() => closeConfirmDialog()}
        open={isConfirmDialogOpen}
        confirmText="Yes, cancel"
        cancelText="No, take me back"
        title="Are you sure you want to cancel your Subscription?"
        subtitle={`We’d hate to see you leave but if you do choose to cancel, your subscription will continue until ${
          subscriptionEndDate || 'the end of your billing period'
        }, when any final payment due will be taken. You will no longer have access to your Vyce data thereafter.`}
        handleConfirm={() => cancelSubscription()}
      />

      <CancelSubscriptionReasonDialog
        onClose={closeCancelReasonDialog}
        open={isCancelReasonDialogOpen}
        endsOn={subscriptionEndDate}
      />

      <ActivateSubscriptionDialog
        open={isActivateSubscriptionDialogOpen}
        onClose={closeActivateSubscriptionDialog}
        callback={subscribe}
        loading={loading}
        wasSubscribed={wasSubscribed}
      />

      <ManageSubscriptionDialog
        handleCancelClick={openConfirmDialog}
        open={isManageDialogOpen}
        handleUpdatePaymentsClick={openLoginDialog}
        handleClose={closeManageDialog}
      />

      {email && (
        <VerifyYourIdentityDialog
          email={email}
          open={isLoginDialogOpen}
          handleClose={closeLoginDialog}
          handleNext={handleNext}
        />
      )}
    </>
  );
};
